import React, { useState, useEffect, useRef, useContext } from 'react';
import {
    Typography,
    TextField,
    InputAdornment,
    Grid,
    Stack,
    LinearProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import styles from '../../Styles/ComponentStyles/AIPdfExtractor/AIPdfExtractor.module.scss';
import GlobalButton from '../../Utils/GlobalUtilsComponents/GlobalButton';
import uploadIcon from "../../assets/Images/Profile/uploadIcon.svg"
import pdfIcon from "../../assets/Images/Profile/pdfIcon.svg"
import successTick from "../../assets/Images/Profile/successTick.svg"
import { ProfileContext, ProfileContextPayload } from '../../Context/ProfileDetailsContext';
import { useSnackbar } from '../GlobalSnackbar/GlobalSnackbar';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import { Divider } from '@mui/material';
import { DataGrid, GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import moment from 'moment';
import CustomPagination from '../CustomPagination/CustomPagination';

// Types
interface FileData {
    file_name: string;
    file_total_page: string;
    file_size: string;
    file_created_at: string;
    file_id: string;
    file_url: string
}

const FileSelectionComponent: React.FC = () => {

    const {
        getUploadedFilesAPI,
        setFlagImportBtnClicked,
        uploadPdfForExtractionAPI,
        getSelectedPdfImagesAPI,
        setExtractedFileData,
        deletePdfFileAPI,
        setSelectedPages,
        setExtractedTablesData,
        setExtractedTextData,
        setEditableData,
        setSelectedFields,
        setSelectedColumns
    } = useContext(ProfileContext) as ProfileContextPayload
    const {
        setFlagLoader,
    } = useContext(UserContext) as UserContextPayload

    const { showSnackbar } = useSnackbar();

    const [files, setFiles] = useState<FileData[]>([]);
    const [filteredFiles, setFilteredFiles] = useState<FileData[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [uploadedFileData, setUploadedFileData] = useState<any>(null);
    const [isUploading, setIsUploading] = useState(false);

    const fileColumns: any = [
        {
            field: "file_name",
            headerName: "FILE NAME",
            align: "left",
            headerAlign: "left",
            width: 440,
        },
        {
            field: "file_size",
            headerName: "FILE SIZE",
            align: "left",
            headerAlign: "left",
            width: 400,
        },
        {
            field: "file_total_page",
            headerName: "PAGES",
            align: "left",
            headerAlign: "left",
            width: 400,
        },
        {
            field: "file_created_at",
            headerName: "CREATED DATE",
            valueGetter: (params: any) =>
                params?.row?.file_created_at ? moment(params?.row?.file_created_at).format("MM/DD/YYYY hh:mm A") : "",
            align: "left",
            headerAlign: "left",
            width: 220,
        },
        {
            field: "extractData", // <-- Use a unique field name
            headerName: "",
            align: "left",
            headerAlign: "left",
            sortable: false,
            disableColumnMenu: true,
            width: 170,
            renderCell: (params: any) => {
                return (
                    <GlobalButton
                        value={"Extract Data"}
                        size="medium"
                        className={styles.saveBtn}
                        onClick={() => handleExtractDataClick(params?.row?.file_id)}
                    />
                );
            },
        },
        {
            field: "delete", // <-- Use a unique field name
            headerName: "",
            align: "left",
            headerAlign: "left",
            sortable: false,
            disableColumnMenu: true,
            width: 50,
            renderCell: (params: any) => {
                const file_id = params?.row?.file_id
                return (
                    <>
                        <Grid sx={{ cursor: "pointer" }} item onClick={() => handleFileDeletion(file_id)}>
                            <DeleteIcon />
                        </Grid>
                    </>
                )
            },
        },
    ];

    const fetchUploadedFileDetails = async () => {
        try {
            setFlagLoader(true)
            const response = await getUploadedFilesAPI();
            if (response?.status) {
                setFiles(response?.data)
            } else {
                showSnackbar(response?.error?.message || "Something went wrong!", "error")
            }
            setFlagLoader(false)
        } catch (error) {
            console.error('Error fetching files:', error);
            setFlagLoader(false)
        }
    }

    useEffect(() => {
        const loadFiles = async () => {
            try {
                await fetchUploadedFileDetails();
            } catch (error) {
                console.error('Error fetching files:', error);
            }
        };
        loadFiles();
    }, []);

    useEffect(() => {
        emptyContext()
    }, [])

    useEffect(() => {
        // Search across multiple fields with case-insensitive matching
        const filterFiles = () => {
            if (searchTerm?.trim() === '') {
                setFilteredFiles(files);
                return;
            }

            const lowercasedSearchTerm = searchTerm?.toLowerCase()?.trim();

            const filtered = files?.filter(file =>
                file?.file_name?.toLowerCase()?.includes(lowercasedSearchTerm)
                // file.file_size.toLowerCase().includes(lowercasedSearchTerm) ||
                // file.file_total_pages.toLowerCase().includes(lowercasedSearchTerm) ||
                // file.file_created_at.toLowerCase().includes(lowercasedSearchTerm)
            );

            setFilteredFiles(filtered);
        };

        filterFiles();
    }, [searchTerm, files]);

    function NoRowsOverlay() {
        return (
            <Stack height="100%" alignItems="center" justifyContent="center" color="#0F172B">
                No Data Found
            </Stack>
        );
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    margin: '5px 0px 25px',
                    float: 'right',
                    '& .MuiButtonBase-root': {
                        color: '#ffffff !important',
                    },
                }}
            >
                <GridToolbar />
            </GridToolbarContainer>
        );
    };

    const emptyContext = () => {
        setExtractedFileData(null);
        setSelectedPages([]);
        setExtractedTablesData([]);
        setExtractedTextData({
            IULFeeDetails: "",
            IULAccountValueDetail: "",
            IULDeathBenifits: ""
        });
        setEditableData([]);
        setSelectedFields({});
        setSelectedColumns({})
    }

    const handleFileSelection = async (file: File) => {
        const maxSize = 15 * 1024 * 1024; // 15MB in bytes

        if (file.type !== "application/pdf") {
            showSnackbar("Only PDF files are allowed!", "error");
            return;
        }

        if (file.size > maxSize) {
            showSnackbar("File size must be 15MB or smaller!", "error");
            return;
        }
        // setSelectedFile(file);

        await uploadPdf(file);
    };

    const handleFileDeletion = async (file_id: number) => {
        try {
            setFlagLoader(true)
            const response = await deletePdfFileAPI(file_id)
            if (response?.status) {
                showSnackbar("File deleted successfully!", "success");
                fetchUploadedFileDetails()
            } else {
                showSnackbar(response?.error?.message || "Something went wrong!", "error")
            }
        } catch (error) {
            console.log("Error deleting file!", error)
        } finally {
            setFlagLoader(false)
        }
    }

    const uploadPdf = async (file: any) => {
        const formData = new FormData();
        formData.append("file", file, file.name);

        setIsUploading(true);

        try {
            const response = await uploadPdfForExtractionAPI(formData);
            if (response?.status) {
                setUploadedFileData(response?.data?.[0]);
                showSnackbar("File uploaded successfully!", "success");
            } else {
                showSnackbar(response?.error?.message || "Something went wrong!", "error");
            }
        } catch (error) {
            console.log(error, "Error in upload pdf API")
        } finally {
            setIsUploading(false);
        }
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = e.dataTransfer.files;

        if (files.length > 0) {
            handleFileSelection(files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileSelection(e.target.files[0]);
        }
    };

    const handleExtractDataClick = async (fileId: any) => {
        try {
            setFlagLoader(true)
            if (fileId) {
                const response = await getSelectedPdfImagesAPI(fileId)
                if (response?.status) {
                    setExtractedFileData(response?.data?.[0])
                    setFlagImportBtnClicked(true)
                } else {
                    showSnackbar(response?.error?.message || "Something went wrong!", "error")
                }
            } else {
                showSnackbar("Please select a file to extract data", "error")
            }
        } catch (error) {
            console.log(error, "Error in getImages API")
        } finally {
            setFlagLoader(false)
        }
    }

    return (
        <div className={styles.pdfExtractorContainer}>
            <div className={styles.mainContainer}>
                <div>
                    {
                        uploadedFileData ?
                            <>
                                <div className={styles.uploadedSection} >
                                    <Grid container justifyContent={"space-between"}>
                                        <Grid item>
                                            <Grid container gap="15px" alignItems={"center"}>
                                                <Grid item>
                                                    <img src={pdfIcon} alt='pdfIcon'></img>
                                                </Grid>
                                                <Grid item maxWidth={"300px"}>
                                                    <span className={styles.selectedFileText}>{uploadedFileData?.file_name}</span>
                                                </Grid>
                                                <Grid item>
                                                    <span className={styles.selectedFileSize}>{uploadedFileData?.file_size}</span>
                                                </Grid>
                                                <Grid item>
                                                    <img src={successTick} alt='' />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Grid container gap="15px" alignItems={"center"}>
                                                <Grid item sx={{ cursor: "pointer" }} onClick={() => setUploadedFileData(null)}>
                                                    <CloseIcon />
                                                </Grid>
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    style={{
                                                        height: 'auto',
                                                        color: "rgba(175, 175, 175, 1)",
                                                    }}
                                                />
                                                <Grid item>
                                                    <GlobalButton
                                                        value={"Extract Data"}
                                                        size='medium'
                                                        className={styles.saveBtn}
                                                        onClick={() => handleExtractDataClick(uploadedFileData?.file_id)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            </>
                            :
                            <>
                                <div className={styles.uploadSection} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} >
                                    <img src={uploadIcon} alt='uploadIcon'></img>
                                    <div className={styles.uploadTextContainer}>
                                        <Typography variant="body1" className={styles.uploadPrimaryText}>
                                            Drop your files here or <label
                                                htmlFor="file-input"
                                                className={styles.browseText}
                                            >
                                                Browse
                                            </label>
                                        </Typography>
                                        <Typography variant="body2" className={styles.uploadSecondaryText}>
                                            Only PDF file supported. Max file size 15MB
                                        </Typography>
                                    </div>
                                    <input
                                        type="file"
                                        id="file-input"  // Add this id
                                        style={{ display: 'none' }}
                                        accept=".pdf"
                                        onChange={handleFileInputChange}
                                    />
                                </div>
                                {isUploading && <LinearProgress />}
                            </>
                    }
                    <div className={styles.filesContainer}>
                        <Typography variant="subtitle1" className={styles.savedFilesHeader}>
                            Saved Files
                            <TextField
                                placeholder="Search files"
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={handleSearch}
                                className={styles.searchField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm ? (
                                        <InputAdornment
                                            position="end"
                                            sx={{ cursor: 'pointer' }}
                                            onClick={clearSearch}
                                        >
                                            <CloseIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Typography>
                        <div>
                            <DataGrid
                                className={styles.filesTable}
                                disableColumnFilter
                                disableColumnMenu
                                disableColumnSelector
                                disableDensitySelector
                                disableSelectionOnClick
                                rows={filteredFiles}
                                columns={fileColumns}
                                getRowId={(userData: any) => (userData.file_id)}
                                density="compact"
                                hideFooterSelectedRowCount
                                pageSize={10}
                                rowHeight={70}
                                components={{
                                    Pagination: CustomPagination,
                                    Toolbar: CustomToolbar, NoRowsOverlay
                                }}
                                autoHeight
                                sx={{
                                    "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "#ffffff",
                                        color: 'rgba(70, 78, 95, 1)',
                                        margin: "10px !important",
                                        borderRadius: '6px',
                                        borderBottom: "none",
                                        padding: "10px"
                                    },
                                    "& .MuiDataGrid-main ": {
                                        padding: "20px 20px 0px 20px",
                                        borderRadius: "10px",
                                    },
                                    "& .MuiDataGrid-footerContainer": {
                                        borderTop: "none !important",
                                    },
                                    '& .MuiDataGrid-columnSeparator': {
                                        visibility: 'hidden'
                                    },
                                    "& .MuiTablePagination-actions .MuiIconButton-root": {
                                        color: "#fff !important"
                                    },
                                    "& .MuiTablePagination-actions .MuiIconButton-root .MuiSvgIcon-root": {
                                        color: "#fff !important"
                                    },
                                    "& .MuiDataGrid-columnHeader--moving": {
                                        backgroundColor: "transparent !important"
                                    },
                                    "& .MuiDataGrid-cell": {
                                        borderBottom: "0.1px solid rgba(224, 224, 224, 1)"
                                    },
                                    "& .MuiDataGrid-cell:focus": {
                                        outline: "none !important"
                                    },
                                    "& .MuiDataGrid-overlay": {
                                        display: "none !important",
                                        backgroundColor: "transparent !important"
                                    },
                                    "& .MuiDataGrid-toolbarContainer": {
                                        margin: "0px 0px 0px 0px"
                                    },
                                    "& .MuiDataGrid-virtualScroller": {
                                        marginTop: "30px !important"
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FileSelectionComponent;